import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import {
  selectActiveTabId,
  selectActiveTabType,
  selectCharacteristicsCurrentContractActive,
  selectConfig,
  selectContractDetailsCurrentContractActive,
  selectCurrentContractActive,
  selectPreferencesContactSubscriptionData
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectAuthData, selectExtraRoutesAuthData } from '../../../Login/AuthSelectors'

import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import {
  addContractErrors,
  addRecentBanking,
  updateClientData,
  updateContractConfigData
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { generateParameters } from '@/shared/tools/contractSagaHelper'
import { selectAddressCurrentContractActive } from '../SubscriptionFormSelectors'
import { resetContractErrors } from '../SubscriptionFormSlice'
import * as Sentry from '@sentry/react'
import { DURATION } from 'baseui/snackbar'

export const commonActions = {
  CONTRACT_SAVE: 'CONTRACT_SAVE',
  RESEND_PROVIDER_EMAIL: 'RESEND_PROVIDER_EMAIL',
}

export function* saveContract({ enqueue, action, closeModal }) {
  const typeRaw = yield select(selectActiveTabType)
  const type = typeRaw?.toLowerCase()
  const { contactId } = yield select(selectConfig)
  const {
    addressId: contractAddressId,
    billingAddressId: contractBillingAddressId,
    residenceAddressId: contractResidenceAddressId,
    elec = {},
    gas = {},
    propane = {},
    billing = {},
    consumption = {},
  } = yield select(selectCurrentContractActive)
  const contractId = yield select(selectActiveTabId)
  const { saveContractUrl } = yield select(selectExtraRoutesAuthData)
  const contractData = yield select(selectContractDetailsCurrentContractActive)
  const characteristicsData = yield select(selectCharacteristicsCurrentContractActive)
  const contactPreferences = yield select(selectPreferencesContactSubscriptionData)
  const contractAddressData = yield select(selectAddressCurrentContractActive)
  const { country } = yield select(selectAuthData)

  let formParameters

  try {
    formParameters = yield call(generateParameters, {
      type,
      contractId,
      contactId,
      addressId: contractAddressId,
      billingAddressId: contractBillingAddressId,
      residenceAddressId: contractResidenceAddressId,
      contractData,
      characteristicsData,
      contactPreferences,
      action,
      contractAddressData,
      electricityMeterData: elec ?? {},
      gasMeterData: gas ?? {},
      propaneData: propane ?? {},
      billingData: billing ?? {},
      energyConsumptionData: consumption ?? {},
      country,
    })
  } catch (err) {
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: err.message || `Unknown type: ${type}`,
    })
    return
  }

  if (!formParameters || typeof formParameters.entries !== 'function') {
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: `Couldn't generate parameters for type: ${type}`,
    })
    return
  }

  const formParametersObject = Object.fromEntries(formParameters.entries())

  yield put(
    updateContractConfigData({
      id: contractId,
      data: {
        isSaving: true,
      },
    }),
  )
  yield put(resetContractErrors({ id: contractId }))

  try {
    const result = yield call(callApi, {
      endpoint: saveContractUrl,
      enqueue,
      showErrorSnackbar: true,
      payload: formParametersObject,
      method: 'POST',
    })
    const isSuccessAndComplete = action === 'save_final' && Object.keys(result.status_info || {}).length === 0
    yield put(
      updateContractConfigData({
        id: contractId,
        data: {
          isSaving: false,
          completeStatusInfo: result.status_info || null,
          contractLastSaveValidate: isSuccessAndComplete ? new Date().toISOString() : null,
        },
      }),
    )
    if (result.provider_warning) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.WARNING,
        message: result.provider_warning,
        duration: DURATION.long,
      })
    }
    if (action === 'save_final' && Object.keys(result.status_info || {}).length > 0) {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.WARNING,
        message: 'Contrat enregistré mais des informations sont manquantes pour le finaliser',
      })
    } else {
      yield call(showSnackbar, enqueue, {
        type: SNACKBAR_TYPES.SUCCESS,
        message: result.message ?? 'Unexpected save response message',
      })
    }

    if (action === 'save_final' && typeof closeModal === 'function') {
      closeModal(false)
    }

    if (billing.iban) {
      yield put(addRecentBanking({ iban: billing.iban, bic: billing.bic }))
    }

    if (formParametersObject.create_reviews_interaction === '1') {
      yield put(
        updateClientData({
          data: { isReviewRegistered: true, createReviewsInteraction: null, reviewLanguage: null },
          name: 'preferences',
        }),
      )
    }
  } catch (error) {
    yield put(
      updateContractConfigData({
        id: contractId,
        data: {
          isSaving: false,
        },
      }),
    )
    yield put(addContractErrors({ id: contractId, data: error.validationErrors }))
    if (typeof closeModal === 'function') {
      closeModal(false)
    }
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(error, { extra: { context: 'saveContractSaga' } })
    }
  }
}


export function* resendProviderEmail({ payload, callbackError, callbackSuccess }) {
  const contractId = yield select(selectActiveTabId)
  const { resendProviderEmailUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const url = resendProviderEmailUrl?.replace('*', contractId)
    yield call(callApi, {
      endpoint: url,
      payload,
      method: 'POST',
    })
    if (callbackSuccess && typeof callbackSuccess === 'function') {
      callbackSuccess()
    }
  } catch (error) {
    if (callbackError && typeof callbackError === 'function') {
      callbackError(error)
    }
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(error, { extra: { context: 'resendProviderEmailSaga' } })
    }
  }
}
